import React, { useState, useEffect, useCallback } from "react";
import { processService } from "../../services/process.service";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, Plus } from "lucide-react";

const TIPOS_HERDEIRO = ["cônjuge", "filho(a)", "pai/mãe", "irmão/irmã", "outro"];
const TIPO_CONTA_OPTIONS = ["corrente", "poupanca"];
const TIPO_PIX_OPTIONS = ["cpf", "email", "telefone", "aleatorio"];

function Field({ label, children, optional = false }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {label}
                {optional && <span className="text-muted-foreground font-normal normal-case ml-1 text-xs">(opcional)</span>}
            </Label>
            {children}
        </div>
    );
}

function HerdeiroEditDialog({ isOpen, onOpenChange, cedente, herdeiro, onSuccess }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (herdeiro) {
            setForm({
                nome: herdeiro.nome || "",
                cpf: herdeiro.cpf || "",
                tipo_herdeiro: herdeiro.tipo_herdeiro || "",
                percentual: herdeiro.percentual != null ? String(herdeiro.percentual) : "",
                banco: herdeiro.banco || "",
                agencia: herdeiro.agencia || "",
                conta_corrente: herdeiro.conta_corrente || "",
                digito_conta: herdeiro.digito_conta || "",
                tipo_conta: herdeiro.tipo_conta || "",
                pix: herdeiro.pix || "",
                tipo_pix: herdeiro.tipo_pix || "",
                endereco_logradouro: herdeiro.endereco_logradouro || "",
                endereco_numero: herdeiro.endereco_numero || "",
                endereco_complemento: herdeiro.endereco_complemento || "",
                endereco_bairro: herdeiro.endereco_bairro || "",
                endereco_cidade: herdeiro.endereco_cidade || "",
                endereco_estado: herdeiro.endereco_estado || "",
                endereco_cep: herdeiro.endereco_cep || "",
            });
        }
    }, [herdeiro]);

    const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            Object.entries(form).forEach(([k, v]) => {
                if (v !== "" && v !== null && v !== undefined) payload[k] = v;
            });
            if (payload.percentual !== undefined) payload.percentual = parseFloat(payload.percentual);
            await processService.updateHerdeiro(cedente.cedente_id, herdeiro.id, payload);
            toast.success("Herdeiro atualizado");
            onSuccess();
            onOpenChange(false);
        } catch (err) {
            toast.error("Erro ao atualizar herdeiro: " + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar Herdeiro</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 py-2">
                    <Field label="Nome">
                        <Input value={form.nome || ""} onChange={set("nome")} className="h-8 text-sm" />
                    </Field>
                    <Field label="CPF">
                        <Input value={form.cpf || ""} onChange={set("cpf")} className="h-8 text-sm" />
                    </Field>
                    <Field label="Tipo de Herdeiro">
                        <select value={form.tipo_herdeiro || ""} onChange={set("tipo_herdeiro")} className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm">
                            <option value="">Selecione...</option>
                            {TIPOS_HERDEIRO.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="Percentual (%)">
                        <Input type="number" min="0" max="100" step="0.01" value={form.percentual || ""} onChange={set("percentual")} className="h-8 text-sm" />
                    </Field>

                    <div className="col-span-2 pt-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dados Bancários</p>
                    </div>
                    <Field label="Banco" optional><Input value={form.banco || ""} onChange={set("banco")} className="h-8 text-sm" /></Field>
                    <Field label="Agência" optional><Input value={form.agencia || ""} onChange={set("agencia")} className="h-8 text-sm" /></Field>
                    <Field label="Conta" optional><Input value={form.conta_corrente || ""} onChange={set("conta_corrente")} className="h-8 text-sm" /></Field>
                    <Field label="Dígito" optional><Input value={form.digito_conta || ""} onChange={set("digito_conta")} className="h-8 text-sm" /></Field>
                    <Field label="Tipo de Conta" optional>
                        <select value={form.tipo_conta || ""} onChange={set("tipo_conta")} className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm">
                            <option value="">Selecione...</option>
                            {TIPO_CONTA_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="PIX" optional><Input value={form.pix || ""} onChange={set("pix")} className="h-8 text-sm" /></Field>
                    <Field label="Tipo de PIX" optional>
                        <select value={form.tipo_pix || ""} onChange={set("tipo_pix")} className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm">
                            <option value="">Selecione...</option>
                            {TIPO_PIX_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>

                    <div className="col-span-2 pt-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Endereço</p>
                    </div>
                    <Field label="Logradouro" optional><Input value={form.endereco_logradouro || ""} onChange={set("endereco_logradouro")} className="h-8 text-sm" /></Field>
                    <Field label="Número" optional><Input value={form.endereco_numero || ""} onChange={set("endereco_numero")} className="h-8 text-sm" /></Field>
                    <Field label="Complemento" optional><Input value={form.endereco_complemento || ""} onChange={set("endereco_complemento")} className="h-8 text-sm" /></Field>
                    <Field label="Bairro" optional><Input value={form.endereco_bairro || ""} onChange={set("endereco_bairro")} className="h-8 text-sm" /></Field>
                    <Field label="Cidade" optional><Input value={form.endereco_cidade || ""} onChange={set("endereco_cidade")} className="h-8 text-sm" /></Field>
                    <Field label="Estado" optional><Input value={form.endereco_estado || ""} onChange={set("endereco_estado")} maxLength={2} className="h-8 text-sm" /></Field>
                    <Field label="CEP" optional><Input value={form.endereco_cep || ""} onChange={set("endereco_cep")} className="h-8 text-sm" /></Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Salvar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddHerdeiroDialog({ isOpen, onOpenChange, cedenteId, existingSum, onSuccess }) {
    const [form, setForm] = useState({ nome: "", cpf: "", tipo_herdeiro: "", percentual: "", banco: "", agencia: "", conta_corrente: "", digito_conta: "", tipo_conta: "", pix: "", tipo_pix: "", endereco_logradouro: "", endereco_numero: "", endereco_complemento: "", endereco_bairro: "", endereco_cidade: "", endereco_estado: "", endereco_cep: "" });
    const [saving, setSaving] = useState(false);
    const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

    const pct = parseFloat(form.percentual) || 0;
    const newSum = existingSum + pct;

    const handleSave = async () => {
        if (newSum > 100) return;
        setSaving(true);
        try {
            const payload = {};
            Object.entries(form).forEach(([k, v]) => { if (v !== "") payload[k] = v; });
            if (payload.percentual) payload.percentual = parseFloat(payload.percentual);
            await processService.createHerdeiro(cedenteId, payload);
            toast.success("Herdeiro adicionado");
            onSuccess();
            onOpenChange(false);
        } catch (err) {
            toast.error("Erro: " + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Adicionar Herdeiro</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 py-2">
                    <Field label="Nome"><Input value={form.nome} onChange={set("nome")} className="h-8 text-sm" /></Field>
                    <Field label="CPF"><Input value={form.cpf} onChange={set("cpf")} className="h-8 text-sm" /></Field>
                    <Field label="Tipo">
                        <select value={form.tipo_herdeiro} onChange={set("tipo_herdeiro")} className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm">
                            <option value="">Selecione...</option>
                            {TIPOS_HERDEIRO.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="Percentual (%)">
                        <Input type="number" min="0" max="100" step="0.01" value={form.percentual} onChange={set("percentual")} className={`h-8 text-sm ${newSum > 100 ? "border-red-400" : ""}`} />
                        {newSum > 100 && <p className="text-xs text-red-500">Soma ultrapassa 100% ({existingSum.toFixed(2)}% já cadastrado)</p>}
                    </Field>
                    <Field label="Banco" optional><Input value={form.banco} onChange={set("banco")} className="h-8 text-sm" /></Field>
                    <Field label="Agência" optional><Input value={form.agencia} onChange={set("agencia")} className="h-8 text-sm" /></Field>
                    <Field label="Conta" optional><Input value={form.conta_corrente} onChange={set("conta_corrente")} className="h-8 text-sm" /></Field>
                    <Field label="Dígito" optional><Input value={form.digito_conta} onChange={set("digito_conta")} className="h-8 text-sm" /></Field>
                    <Field label="Tipo de Conta" optional>
                        <select value={form.tipo_conta} onChange={set("tipo_conta")} className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm">
                            <option value="">Selecione...</option>
                            {TIPO_CONTA_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="PIX" optional><Input value={form.pix} onChange={set("pix")} className="h-8 text-sm" /></Field>
                    <Field label="Tipo PIX" optional>
                        <select value={form.tipo_pix} onChange={set("tipo_pix")} className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm">
                            <option value="">Selecione...</option>
                            {TIPO_PIX_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <div className="col-span-2 pt-1"><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Endereço</p></div>
                    <Field label="Logradouro" optional><Input value={form.endereco_logradouro} onChange={set("endereco_logradouro")} className="h-8 text-sm" /></Field>
                    <Field label="Número" optional><Input value={form.endereco_numero} onChange={set("endereco_numero")} className="h-8 text-sm" /></Field>
                    <Field label="Complemento" optional><Input value={form.endereco_complemento} onChange={set("endereco_complemento")} className="h-8 text-sm" /></Field>
                    <Field label="Bairro" optional><Input value={form.endereco_bairro} onChange={set("endereco_bairro")} className="h-8 text-sm" /></Field>
                    <Field label="Cidade" optional><Input value={form.endereco_cidade} onChange={set("endereco_cidade")} className="h-8 text-sm" /></Field>
                    <Field label="Estado" optional><Input value={form.endereco_estado} onChange={set("endereco_estado")} maxLength={2} className="h-8 text-sm" /></Field>
                    <Field label="CEP" optional><Input value={form.endereco_cep} onChange={set("endereco_cep")} className="h-8 text-sm" /></Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
                    <Button onClick={handleSave} disabled={saving || newSum > 100}>
                        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Adicionar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function HerdeirosTab({ processoId }) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null); // { cedente, herdeiro }
    const [addTarget, setAddTarget] = useState(null);   // cedente group

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await processService.getHerdeirosByProcesso(processoId);
            setGroups(data);
        } catch {
            toast.error("Erro ao carregar herdeiros");
        } finally {
            setLoading(false);
        }
    }, [processoId]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (cedenteId, herdeiroId) => {
        if (!window.confirm("Confirma excluir este herdeiro?")) return;
        try {
            await processService.deleteHerdeiro(cedenteId, herdeiroId);
            toast.success("Herdeiro excluído");
            load();
        } catch (err) {
            toast.error("Erro ao excluir: " + err.message);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="text-center text-muted-foreground py-12">
                Nenhum substituído falecido com herdeiros cadastrados neste processo.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {groups.map(group => {
                const existingSum = group.herdeiros.reduce((s, h) => s + (parseFloat(h.percentual) || 0), 0);
                const pctColor = existingSum > 100 ? "text-red-600" : existingSum === 100 ? "text-green-700" : "text-amber-600";

                return (
                    <Card key={group.cedente_id} className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <span className="font-semibold text-foreground">{group.cedente_nome}</span>
                                <span className="text-sm text-muted-foreground ml-2">({group.cedente_cpf})</span>
                                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-100">Falecido</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${pctColor}`}>
                                    Total: {existingSum.toFixed(2).replace(".", ",")}%
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setAddTarget(group)}
                                    disabled={existingSum >= 100}
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Adicionar herdeiro
                                </Button>
                            </div>
                        </div>

                        {group.herdeiros.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">Nenhum herdeiro cadastrado.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-xs text-muted-foreground uppercase">
                                            <th className="text-left py-1 pr-3">Nome</th>
                                            <th className="text-left py-1 pr-3">CPF</th>
                                            <th className="text-left py-1 pr-3">Tipo</th>
                                            <th className="text-right py-1 pr-3">%</th>
                                            <th className="text-left py-1 pr-3">Banco / Conta</th>
                                            <th className="text-left py-1 pr-3">PIX</th>
                                            <th className="text-left py-1 pr-3">Endereço</th>
                                            <th className="text-right py-1">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.herdeiros.map(h => (
                                            <tr key={h.id} className="border-b last:border-0 hover:bg-muted">
                                                <td className="py-2 pr-3 font-medium">{h.nome || "—"}</td>
                                                <td className="py-2 pr-3 text-muted-foreground">{h.cpf || "—"}</td>
                                                <td className="py-2 pr-3">
                                                    {h.tipo_herdeiro ? (
                                                        <span className="px-1.5 py-0.5 rounded text-xs bg-statusInfoBg text-statusInfo">{h.tipo_herdeiro}</span>
                                                    ) : "—"}
                                                </td>
                                                <td className="py-2 pr-3 text-right font-mono">
                                                    {h.percentual != null ? `${parseFloat(h.percentual).toFixed(2).replace(".", ",")}%` : "—"}
                                                </td>
                                                <td className="py-2 pr-3 text-muted-foreground text-xs">
                                                    {h.banco ? `${h.banco} ag.${h.agencia} c.${h.conta_corrente}-${h.digito_conta}` : "—"}
                                                </td>
                                                <td className="py-2 pr-3 text-muted-foreground text-xs">
                                                    {h.pix ? `${h.tipo_pix || ""}: ${h.pix}` : "—"}
                                                </td>
                                                <td className="py-2 pr-3 text-muted-foreground text-xs">
                                                    {h.endereco_logradouro ? `${h.endereco_logradouro}, ${h.endereco_numero || "s/n"} — ${h.endereco_cidade || ""}/${h.endereco_estado || ""}` : "—"}
                                                </td>
                                                <td className="py-2 text-right space-x-1">
                                                    <Button variant="ghost" size="sm" onClick={() => setEditTarget({ cedente: group, herdeiro: h })}>
                                                        <Pencil className="h-4 w-4 text-blue-500" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(group.cedente_id, h.id)}>
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                );
            })}

            {editTarget && (
                <HerdeiroEditDialog
                    isOpen={!!editTarget}
                    onOpenChange={(open) => { if (!open) setEditTarget(null); }}
                    cedente={editTarget.cedente}
                    herdeiro={editTarget.herdeiro}
                    onSuccess={load}
                />
            )}

            {addTarget && (
                <AddHerdeiroDialog
                    isOpen={!!addTarget}
                    onOpenChange={(open) => { if (!open) setAddTarget(null); }}
                    cedenteId={addTarget.cedente_id}
                    existingSum={addTarget.herdeiros.reduce((s, h) => s + (parseFloat(h.percentual) || 0), 0)}
                    onSuccess={load}
                />
            )}
        </div>
    );
}
